import { useEffect, useRef, useState } from 'react';

const SUPPORTS_INTERSECTION_OBSERVER = typeof IntersectionObserver !== 'undefined';

export interface ViewportPresence<T extends HTMLElement> {
  ref: React.RefObject<T | null>;
  /**
   * True from the first time the element came near the viewport onwards. For
   * work worth doing once and keeping, like fetching the bytes.
   */
  hasBeenNear: boolean;
  /**
   * True only while the element is near the viewport. For anything expensive to
   * hold — a scrolled-past tile should not still own a pdf.js worker.
   */
  isNear: boolean;
}

export const useInViewport = <T extends HTMLElement>(): ViewportPresence<T> => {
  const ref = useRef<T>(null);
  const [isNear, setIsNear] = useState(false);
  const [hasBeenNear, setHasBeenNear] = useState(false);

  useEffect(() => {
    if (!SUPPORTS_INTERSECTION_OBSERVER) return;

    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const near = entries.some((entry) => entry.isIntersecting);
        setIsNear(near);
        if (near) setHasBeenNear(true);
      },
      { rootMargin: '200px' },
    );
    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  if (!SUPPORTS_INTERSECTION_OBSERVER) {
    return { ref, hasBeenNear: true, isNear: true };
  }
  return { ref, hasBeenNear, isNear };
};
